import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ACCOUNT_STATUSES, ACCOUNT_STATUS_LABEL, PLAN_LABEL, statusBadge, fmtDate } from "@/lib/adminMeta";
import { Search, ChevronRight } from "lucide-react";

export default function AdminDealers() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (status && status !== "all") params.status = status;
    try { const { data } = await api.get("/admin/dealers", { params }); setRows(data); }
    catch { setRows([]); } finally { setLoading(false); }
  }, [q, status]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  return (
    <div className="space-y-6 fade-up" data-testid="admin-dealers">
      <h1 className="text-3xl font-bold text-navy">Dealer Accounts</h1>

      <div className="bg-white rounded-2xl border border-border p-4 grid gap-3 sm:grid-cols-3">
        <div className="relative sm:col-span-2">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search dealership, contact, email" className="pl-9" data-testid="dealer-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="dealer-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All Statuses</SelectItem>
            {ACCOUNT_STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-2xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-navy">
              <tr className="text-left">
                {["Dealership", "Primary Contact", "Email", "Plan", "Active Inventory", "Users", "Status", "Created"].map((h) => (
                  <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>
                ))}
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border" data-testid="dealer-table-body">
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">Loading...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">No dealer accounts match your filters.</td></tr>
              ) : rows.map((d) => (
                <tr key={d.id} onClick={() => navigate(`/admin/dealers/${d.id}`)} className="group hover:bg-secondary/40 cursor-pointer" data-testid={`dealer-row-${d.id}`}>
                  <td className="px-4 py-3 font-medium text-navy whitespace-nowrap">{d.name || "-"}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{d.primary_contact || "-"}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{d.email || "-"}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{PLAN_LABEL[d.plan] || d.plan}</td>
                  <td className="px-4 py-3">{d.active_inventory}</td>
                  <td className="px-4 py-3">{d.num_users}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${statusBadge(d.account_status)}`}>{ACCOUNT_STATUS_LABEL[d.account_status] || d.account_status}</span></td>
                  <td className="px-4 py-3 whitespace-nowrap">{fmtDate(d.created_at)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary opacity-60 group-hover:opacity-100 transition-opacity" data-testid={`dealer-view-${d.id}`}>
                      View <ChevronRight className="w-3.5 h-3.5" />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
